import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PRODUCTS } from '../mock/mockData';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { Star, Truck, RotateCcw, ShieldCheck, Minus, Plus, Heart, PlayCircle, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import ProductCarousel from '../components/ProductCarousel';
import { getCatalogProduct, getCatalogProducts } from '../services/catalogService';

const fmt = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN');
const BASE_SELECTION_ID = 'base';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedVariantId = searchParams.get('variant') || '';
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [product, setProduct] = useState(() => PRODUCTS.find((p) => p.id === id));
  const [catalog, setCatalog] = useState(PRODUCTS);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState(BASE_SELECTION_ID);
  const [fullViewOpen, setFullViewOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    setActiveImg(0);
    setSelectedColor('');
    setSelectedSize('');
    setSelectedOptionId(BASE_SELECTION_ID);
    getCatalogProduct(id).then(({ product: loadedProduct }) => {
      if (mounted) setProduct(loadedProduct);
    });
    getCatalogProducts({ limit: 100 }).then(({ products }) => {
      if (mounted) setCatalog(products);
    });
    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    const variants = product?.variants || [];
    const requestedVariant = variants.find((variant) =>
      [variant.id, variant._id, variant.sku].filter(Boolean).map(String).includes(String(requestedVariantId))
    );
    if (requestedVariant) {
      setSelectedOptionId(String(requestedVariant.id || requestedVariant._id || requestedVariant.sku));
      setSelectedColor(requestedVariant.colorName || requestedVariant.color || '');
      setSelectedSize(requestedVariant.size || '');
    } else {
      setSelectedOptionId(BASE_SELECTION_ID);
      setSelectedColor(product?.colorName || product?.color || '');
      setSelectedSize(product?.specification || product?.dimensions || product?.material || 'Main product');
    }
    setActiveImg(0);
  }, [product, requestedVariantId]);

  if (!product) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-20 text-center bg-[#FAF7F2]">
        <p className="text-[#1F2A44]">Product not found.</p>
        <Link to="/shop" className="text-[#E8B530] hover:underline mt-3 inline-block font-semibold">Back to Shop</Link>
      </main>
    );
  }

  const variants = product.variants || [];
  const variantIdOf = (variant, index) => String(variant.id || variant._id || variant.sku || index);
  const selectionOptions = [
    {
      id: BASE_SELECTION_ID,
      type: 'base',
      colorLabel: product.colorName || product.color || 'Main product',
      specLabel: product.specification || product.dimensions || product.material || 'Main product',
      image: product.colorImage || product.image,
      price: product.price,
      stock: product.stock,
      sku: product.sku
    },
    ...variants.map((variant, index) => ({
      id: variantIdOf(variant, index),
      type: 'variant',
      variant,
      colorLabel: variant.colorName || variant.color || 'Variant colour',
      specLabel: variant.size || variant.specification || variant.dimensions || 'Variant specification',
      image: variant.colorImage || variant.images?.[0] || product.image,
      price: variant.price ?? product.price,
      stock: variant.stock ?? product.stock,
      sku: variant.sku || product.sku
    }))
  ];
  const selectedOption = selectionOptions.find((option) => option.id === selectedOptionId) || selectionOptions[0];
  const colorOptions = selectionOptions;
  const specOptions = selectionOptions.filter((option) => option.colorLabel === selectedOption.colorLabel);
  const selectedVariant = selectedOption.type === 'variant' ? selectedOption.variant : null;
  const previewVariant = selectedVariant;
  const canBuy = true;
  const variantImages = previewVariant
    ? (previewVariant.images || []).filter(Boolean)
    : [];
  const effectiveProduct = previewVariant ? {
    ...product,
    price: previewVariant.price ?? product.price,
    marketPrice: previewVariant.marketPrice ?? product.marketPrice,
    stock: previewVariant.stock ?? product.stock,
    sku: previewVariant.sku || product.sku,
    material: previewVariant.material || product.material,
    dimensions: previewVariant.dimensions || product.dimensions,
    specification: previewVariant.specification,
    images: variantImages.length ? [...new Set(variantImages)] : product.images,
    videos: previewVariant.videos?.length ? previewVariant.videos : product.videos,
    image: variantImages[0] || product.image,
    selectedVariantId: selectedVariant?.id || selectedVariant?._id,
    variantLabel: [previewVariant.colorName || previewVariant.color, previewVariant.size].filter(Boolean).join(' / ')
  } : product;
  const images = effectiveProduct.images && effectiveProduct.images.length ? effectiveProduct.images : [effectiveProduct.image];
  const videos = effectiveProduct.videos || [];
  const hasStock = effectiveProduct.stock !== undefined && effectiveProduct.stock !== null;
  const stock = Number(effectiveProduct.stock ?? 0);
  const showLowStock = hasStock && stock < 10;
  const canPurchaseStock = !hasStock || stock > 0;
  const similar = catalog.filter((p) => p.category === product.category && (p.productId || p.id) !== product.id).slice(0, 8);
  const mediaCount = images.length + videos.length;
  const showPrevMedia = () => setActiveImg((current) => (current - 1 + mediaCount) % mediaCount);
  const showNextMedia = () => setActiveImg((current) => (current + 1) % mediaCount);

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-8 lg:gap-12 py-4 sm:py-6">
        {/* Gallery Container - Fully Responsive & Edge-to-Edge Fill */}
        <div className="min-w-0 max-w-lg mx-auto md:max-w-none w-full">
          <div className="relative w-full aspect-square bg-white border border-[#1F2A44]/10 rounded-2xl overflow-hidden shadow-sm flex items-center justify-center p-1 sm:p-2 group">
            {activeImg >= images.length && videos[activeImg - images.length] ? (
              <video src={videos[activeImg - images.length].url} controls className="w-full h-full object-contain rounded-xl" />
            ) : (
              <button type="button" onClick={() => setFullViewOpen(true)} className="w-full h-full flex items-center justify-center overflow-hidden">
                <img
                  src={images[activeImg]}
                  alt={product.name}
                  className="w-full h-full object-contain rounded-xl transition-transform duration-300 group-hover:scale-[1.02]"
                />
              </button>
            )}

            {/* View Full View Lightbox Trigger */}
            <button
              type="button"
              onClick={() => setFullViewOpen(true)}
              className="absolute right-3 bottom-3 z-10 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md border border-[#1F2A44]/10 px-3 py-1.5 text-xs font-bold text-[#1F2A44] shadow-md rounded-xl hover:bg-[#E8B530] transition"
            >
              <Maximize2 size={14} />
              View ({mediaCount})
            </button>

            {/* Navigation Arrows */}
            {mediaCount > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPrevMedia}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-[#E8B530] text-[#1F2A44] p-2 rounded-full shadow-md transition"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  onClick={showNextMedia}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-[#E8B530] text-[#1F2A44] p-2 rounded-full shadow-md transition"
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails Row */}
          {mediaCount > 1 && (
            <div className="relative mt-3">
              <div className="flex gap-2 sm:gap-3 overflow-x-auto py-1.5 scrollbar-thin">
                {images.map((im, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-16 h-16 sm:w-20 sm:h-20 border-2 rounded-xl bg-white p-1 ${
                      i === activeImg ? 'border-[#E8B530] shadow-sm' : 'border-[#1F2A44]/15'
                    } overflow-hidden hover:border-[#E8B530] transition-all flex-shrink-0`}
                  >
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                {videos.map((video, i) => (
                  <button
                    key={video.url}
                    onClick={() => setActiveImg(images.length + i)}
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 border-2 rounded-xl ${
                      images.length + i === activeImg ? 'border-[#E8B530]' : 'border-[#1F2A44]/15'
                    } overflow-hidden hover:border-[#E8B530] transition-colors bg-[#1F2A44] text-white text-xs flex-shrink-0`}
                  >
                    <video src={video.url} muted className="h-full w-full object-cover opacity-70" />
                    <PlayCircle size={22} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Desktop Product Description Block Below Images */}
          <div className="hidden md:block mt-6 bg-white border border-[#1F2A44]/10 rounded-2xl p-5 sm:p-6 shadow-sm">
            <h3 className="font-serif text-lg font-bold text-[#1F2A44] mb-3">Product Description</h3>
            <div className="text-xs sm:text-sm text-[#1F2A44]/80 leading-relaxed font-serif italic border-l-4 border-[#E8B530] pl-4 py-1">
              "{product.description || 'Bring modern elegance and lasting durability to your home with FOSEN furniture. Expertly crafted from premium engineered wood and high-quality finish, combining contemporary aesthetics with everyday functionality.'}"
            </div>
          </div>
        </div>

        {/* Product Details Column */}
        <div className="min-w-0">
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#1F2A44] leading-tight">{product.name}</h1>
          <div className="flex items-center gap-2 mt-3 text-xs sm:text-sm">
            <div className="flex items-center gap-1 bg-[#E8B530]/15 text-[#1F2A44] px-2.5 py-1 rounded-lg">
              <Star size={13} className="fill-[#E8B530] text-[#E8B530]" />
              <span className="font-bold">{product.rating ? Number(product.rating).toFixed(1) : 4.5}</span>
            </div>
            <span className="text-[#1F2A44]/60">({product.reviews || 0} reviews)</span>
          </div>

          <div className="mt-4 sm:mt-5 flex items-baseline gap-3 flex-wrap">
            <span className="text-2xl sm:text-3xl font-bold text-[#1F2A44]">{fmt(effectiveProduct.price)}</span>
            {effectiveProduct.marketPrice > effectiveProduct.price && (
              <>
                <span className="text-[#1F2A44]/50 line-through text-sm sm:text-base">{fmt(effectiveProduct.marketPrice)}</span>
                <span className="text-green-700 font-bold text-xs sm:text-sm">
                  ({Math.round(((effectiveProduct.marketPrice - effectiveProduct.price) / effectiveProduct.marketPrice) * 100)}% off)
                </span>
              </>
            )}
          </div>
          <p className="text-xs text-[#1F2A44]/60 mt-1">Inclusive of all taxes. Free shipping over ₹10,000.</p>
          {showLowStock && (
            <p className="mt-3 inline-flex bg-red-50 border border-red-200 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold text-red-700">
              {stock > 0 ? `Hurry up, only ${stock} left in stock.` : 'This option is currently out of stock.'}
            </p>
          )}

          {selectionOptions.length > 0 && (
            <div className="mt-5 space-y-4">
              {colorOptions.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Colour / Option</p>
                  <div className="flex flex-wrap gap-2">
                    {colorOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setSelectedColor(option.colorLabel);
                          setSelectedSize(option.specLabel);
                          setActiveImg(0);
                        }}
                        className={`flex min-w-[120px] items-center gap-2 border rounded-xl p-2 text-left text-xs sm:text-sm transition-all ${
                          selectedOptionId === option.id
                            ? 'border-[#E8B530] bg-[#E8B530]/15 font-semibold shadow-sm'
                            : 'border-[#1F2A44]/15 bg-white hover:border-[#E8B530]'
                        }`}
                      >
                        <span className="h-9 w-9 flex-shrink-0 overflow-hidden rounded-lg bg-[#F3EFE8]">
                          {option.image ? <img src={option.image} alt={option.colorLabel} className="h-full w-full object-contain" /> : null}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-[#1F2A44] truncate">{option.colorLabel}</span>
                          <span className="block text-[10px] text-[#1F2A44]/60">{fmt(option.price)}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {specOptions.length > 0 && (
                <div>
                  <p className="text-xs uppercase font-bold text-[#1F2A44]/70 mb-2">Specification</p>
                  <div className="flex flex-wrap gap-2">
                    {specOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedOptionId(option.id);
                          setSelectedColor(option.colorLabel);
                          setSelectedSize(option.specLabel);
                          setActiveImg(0);
                        }}
                        className={`px-3.5 py-2 border rounded-xl text-xs sm:text-sm transition-all ${
                          selectedOptionId === option.id
                            ? 'border-[#E8B530] bg-[#E8B530]/15 font-semibold shadow-sm'
                            : 'border-[#1F2A44]/15 bg-white hover:border-[#E8B530]'
                        }`}
                      >
                        {option.specLabel}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-[#1F2A44]/70 font-medium">Stock: {effectiveProduct.stock ?? 0} | SKU: {effectiveProduct.sku}</p>
            </div>
          )}

          <div className="mt-5 grid grid-cols-2 gap-3 text-xs sm:text-sm">
            <div className="border border-[#1F2A44]/10 rounded-xl p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-[#1F2A44]/60">Material</p>
              <p className="text-[#1F2A44] font-semibold mt-0.5">{effectiveProduct.material || 'Engineered Wood'}</p>
            </div>
            <div className="border border-[#1F2A44]/10 rounded-xl p-3 bg-white">
              <p className="text-[10px] uppercase font-bold text-[#1F2A44]/60">Dimensions</p>
              <p className="text-[#1F2A44] font-semibold mt-0.5">{effectiveProduct.dimensions || 'Standard'}</p>
            </div>
            <div className="border border-[#1F2A44]/10 rounded-xl p-3 bg-white col-span-2">
              <p className="text-[10px] uppercase font-bold text-[#1F2A44]/60">Warranty</p>
              <p className="text-[#1F2A44] font-semibold mt-0.5">{product.warranty || '1 Year Brand Warranty'}</p>
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="flex items-center border border-[#1F2A44]/20 rounded-xl bg-white overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-[#F3EFE8] transition-colors text-[#1F2A44]"><Minus size={14} /></button>
              <span className="w-10 text-center text-[#1F2A44] font-bold text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-[#F3EFE8] transition-colors text-[#1F2A44]"><Plus size={14} /></button>
            </div>
            <button className="w-11 h-11 border border-[#1F2A44]/20 rounded-xl bg-white hover:bg-[#E8B530] hover:border-[#E8B530] flex items-center justify-center text-[#1F2A44] transition-colors shadow-sm" aria-label="wishlist">
              <Heart size={18} />
            </button>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <button
              disabled={!canBuy || !canPurchaseStock}
              onClick={() => { addToCart(effectiveProduct, qty); toast({ title: 'Added to cart', description: effectiveProduct.name }); }}
              className="flex-1 bg-[#1F2A44] hover:bg-[#2F3B58] text-[#FAF7F2] font-semibold py-3.5 rounded-xl uppercase tracking-wider text-xs sm:text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 shadow-md"
            >
              Add to Cart
            </button>
            <button
              disabled={!canBuy || !canPurchaseStock}
              onClick={() => { addToCart(effectiveProduct, qty); navigate('/cart'); }}
              className="flex-1 bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-bold py-3.5 rounded-xl uppercase tracking-wider text-xs sm:text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 shadow-md"
            >
              Buy Now
            </button>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3 text-xs text-[#1F2A44]">
            <div className="flex flex-col items-center text-center p-3 rounded-xl border border-[#1F2A44]/10 bg-white">
              <Truck size={18} className="text-[#E8B530] mb-1" />
              <span className="font-semibold">Free Shipping</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 rounded-xl border border-[#1F2A44]/10 bg-white">
              <RotateCcw size={18} className="text-[#E8B530] mb-1" />
              <span className="font-semibold">7-Day Returns</span>
            </div>
            <div className="flex flex-col items-center text-center p-3 rounded-xl border border-[#1F2A44]/10 bg-white">
              <ShieldCheck size={18} className="text-[#E8B530] mb-1" />
              <span className="font-semibold">Brand Warranty</span>
            </div>
          </div>

          {/* Mobile Only Product Description Block - Below Buy Actions & Warranty Badges */}
          <div className="block md:hidden mt-6 bg-white border border-[#1F2A44]/10 rounded-2xl p-5 sm:p-6 shadow-sm">
            <h3 className="font-serif text-lg font-bold text-[#1F2A44] mb-3">Product Description</h3>
            <div className="text-xs sm:text-sm text-[#1F2A44]/80 leading-relaxed font-serif italic border-l-4 border-[#E8B530] pl-4 py-1">
              "{product.description || 'Bring modern elegance and lasting durability to your home with FOSEN furniture. Expertly crafted from premium engineered wood and high-quality finish, combining contemporary aesthetics with everyday functionality.'}"
            </div>
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="border-t border-[#1F2A44]/10 bg-[#FAF7F2]">
          <ProductCarousel title="Related products" products={similar} alwaysShowButtons={true} />
        </div>
      )}

      {/* Full-Screen Image Lightbox Modal */}
      {fullViewOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md text-white">
          <button
            type="button"
            onClick={() => setFullViewOpen(false)}
            className="absolute right-5 top-5 z-10 rounded-full bg-white/20 p-3 hover:bg-white/30 transition text-white"
            aria-label="Close image viewer"
          >
            <X size={24} />
          </button>
          {mediaCount > 1 && (
            <>
              <button
                type="button"
                onClick={showPrevMedia}
                className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/20 p-3 hover:bg-white/30 transition text-white"
                aria-label="Previous image"
              >
                <ChevronLeft size={30} />
              </button>
              <button
                type="button"
                onClick={showNextMedia}
                className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/20 p-3 hover:bg-white/30 transition text-white"
                aria-label="Next image"
              >
                <ChevronRight size={30} />
              </button>
            </>
          )}
          <div className="flex h-full w-full items-center justify-center p-4 sm:p-10">
            {activeImg >= images.length && videos[activeImg - images.length] ? (
              <video src={videos[activeImg - images.length].url} controls autoPlay className="max-h-[85vh] max-w-[95vw] object-contain" />
            ) : (
              <img src={images[activeImg]} alt={product.name} className="max-h-[85vh] max-w-[95vw] object-contain" />
            )}
          </div>
          {mediaCount > 1 && (
            <div className="absolute bottom-5 left-1/2 flex max-w-[90vw] -translate-x-1/2 gap-2 overflow-x-auto rounded-2xl border border-white/20 bg-black/75 px-4 py-3 shadow-2xl backdrop-blur-md">
              {images.map((image, index) => (
                <button
                  key={image || index}
                  type="button"
                  onClick={() => setActiveImg(index)}
                  className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border-2 bg-white p-0.5 ${
                    activeImg === index ? 'border-[#E8B530]' : 'border-transparent opacity-60'
                  }`}
                >
                  <img src={image} alt="" className="h-full w-full object-contain" />
                </button>
              ))}
              {videos.map((video, index) => (
                <button
                  key={video.url}
                  type="button"
                  onClick={() => setActiveImg(images.length + index)}
                  className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl border-2 ${
                    activeImg === images.length + index ? 'border-[#E8B530]' : 'border-transparent opacity-60'
                  }`}
                >
                  <video src={video.url} muted className="h-full w-full object-cover opacity-80" />
                  <PlayCircle size={18} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </main>
  );
};

export default ProductDetailPage;
